import type { Dispatch, SetStateAction } from 'react';
import { useSingleTemplateData } from './useSingleTemplateData';
import {
  isBlocksTemplate,
  isBlocksTemplateRequiresNamespace,
  isCLITemplate,
  isSelfHostedTemplate,
} from '../selectors/templateType-selectors';
import { ActiveModal } from '../components/TemplatePage/Modals/ModalRenderer';
import { usePageNavigate } from './usePageNavigate';
import { useLoginNavigation } from './useLoginNavigation';
import { REFERRAL_INFO } from '../constants/routes';
import { useTemplatesData } from './useTemplatesData';
import { getFunnelLoginLink } from '../utils/getFunnelLinks';
import { isDiyWorkspace } from '../selectors/workspace-selector';
import { getReturnToTemplateIdFromLogin } from '../utils/getReturnToTemplateIdFromLogin';

export type SetOpenModal = Dispatch<SetStateAction<ActiveModal>>;
export const useCloneTemplate = (setActiveModal: SetOpenModal) => {
  const { template } = useSingleTemplateData();
  const { isUserLoggedIn, workspace } = useTemplatesData();
  const navigateToLoginPage = useLoginNavigation();
  const { openBlocksTemplateFlow } = usePageNavigate();
  const isBlocksWithNamespace = isBlocksTemplateRequiresNamespace(template);
  const checkLoggedInAndWorkspaceForUseTemplate = ({
    referralInfo,
    modal,
    shouldCheckOnWindow = false,
  }: {
    referralInfo: string;
    modal: ActiveModal;
    shouldCheckOnWindow?: boolean;
  }) => {
    return () => {
      if (!isUserLoggedIn || (shouldCheckOnWindow && !window.__USER_ID__)) {
        navigateToLoginPage(referralInfo, template);
      } else if (isDiyWorkspace(workspace)) {
        const { encodedCurrentUrl } = getReturnToTemplateIdFromLogin(
          template.id,
        );
        window.location.assign(getFunnelLoginLink(encodedCurrentUrl));
      } else {
        if (referralInfo === REFERRAL_INFO.BLOCKS_TEMPLATE_FLOW) {
          openBlocksTemplateFlow({
            templateId: template.id,
            templateName: template.title,
          });
        } else {
          setActiveModal(modal);
        }
      }
    };
  };

  if (isBlocksTemplate(template)) {
    return checkLoggedInAndWorkspaceForUseTemplate({
      referralInfo: isBlocksWithNamespace
        ? REFERRAL_INFO.BLOCKS_TEMPLATE_WITH_NAMESPACE_FLOW
        : REFERRAL_INFO.BLOCKS_TEMPLATE_FLOW,
      modal: ActiveModal.BLOCKS,
      shouldCheckOnWindow: isBlocksWithNamespace,
    });
  } else if (isCLITemplate(template)) {
    return () => setActiveModal(ActiveModal.CLI);
  } else if (isSelfHostedTemplate(template)) {
    return checkLoggedInAndWorkspaceForUseTemplate({
      referralInfo: REFERRAL_INFO.SELF_HOSTED_FLOW,
      modal: ActiveModal.SELF_HOSTED,
    });
  }
};
